import React from 'react';
import Papa from 'papaparse';
import { axios } from '../../../../services/axios';
import { downloadCSV } from '../../../../utils/utils';

const DEFAULT_BULK_STATUS = { apiErrors: '' };
const ERROR_MESSAGE = 'Something went wrong. Please try again later.';
const FILENAME = 'FailedCreatingUsers.csv';

const FIELDS_TO_EXPORT = {
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email',
    site: 'Site',
    displayName: 'Display Name',
    apiErrors: 'Errors',
};

export default function useCSVCreateBulkUpdate() {
    const [error, setError] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [succeed, setSucceed] = React.useState([]);
    const [counter, setCounter] = React.useState(0);
    const [errorCounter, setErrorCounter] = React.useState(0);
    const [uploadPayloads, setUploadPayloads] = React.useState([]);

    const transformSubmitData = (payloads) =>
        payloads.map((p) => ({ ...DEFAULT_BULK_STATUS, ...p }));

    const processSubmission = async (payloads) => {
        setLoading(true);
        handleBulkCreate(transformSubmitData(payloads));
    };

    const handleReset = () => {
        setCounter(0);
        setErrorCounter(0);
        setSucceed([]);
        setError([]);
    };

    const handleFinish = ({ errors, responses }) => {
        setError(errors);
        setSucceed(responses);
    };

    const handleBulkCreate = async (payloads) => {
        const errors = [];
        const responses = [];
        setUploadPayloads(payloads);

        const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

        for (let index = 0; index < payloads.length; index++) {
            const payload = payloads[index];

            try {
                if (index > 0) await delay(3000);

                let response;

                if (payload.type === 'createPlatformUser') {
                    const { requiredScope, payload: body } = payload;
                    response = await axios.post('/PlatformUser', body, {
                        headers: { 'X-RequestScope': requiredScope },
                    });
                    if (response.status === 200 || response.status === 201) {
                        response = {
                            data: {
                                email: body.email,
                                status: 'Created',
                            },
                        };
                    }
                }

                responses.push({ ...response });
            } catch (e) {
                errors.push({
                    ...payload,
                    email: payload?.payload?.email ?? '',
                    apiErrors:
                        `${e?.message}\n${e?.response?.data?.message || ''}\n${e?.response?.status || ''}` ||
                        ERROR_MESSAGE,
                });
                setErrorCounter((prev) => prev + 1);
            } finally {
                setCounter((prev) => prev + 1);
                if (index === payloads.length - 1) {
                    handleFinish({ errors, responses });
                }
            }
        }
    };

    const handlePayloadChange = React.useCallback((payloads) => {
        processSubmission(payloads);
    }, []);

    const handleExportFailedData = () => {
        const rows = error.map((item) =>
            Object.keys(FIELDS_TO_EXPORT).reduce((acc, key) => {
                acc[FIELDS_TO_EXPORT[key]] =
                    item?.payload?.[key] ?? item[key] ?? '';
                return acc;
            }, {}),
        );
        const res = Papa.unparse(rows);
        downloadCSV(res, FILENAME);
    };

    return {
        handlePayloadChange,
        loading,
        counter,
        errorCounter,
        error,
        succeed,
        handleReset,
        handleExportFailedData,
        total: uploadPayloads.length,
    };
}
